import { useCallback, useEffect, useMemo } from 'react';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { getSocket } from '@/lib/ws/socket';
import { useConnectionStore } from '@/state/connection.store';
import {
  declareStayHome,
  getAllocation,
  revertStayHome,
  setDisruptionStayNights,
  setGroupStayNights,
  type StayHomeInput,
  type StayHomeResult,
  type StayNightsScope,
} from './api';
import {
  agreedNights,
  previewStayConflicts,
  type CaseStayGroupView,
  type StayConflictPreview,
  type StayMismatchView,
  type StayNightsResult,
  type StayPlanChangedEvent,
  type StayPlanView,
} from './stay';
import type { AllocationState } from './types';

// Reading + writing the hotel STAY (feature `stay-nights`, slice N8).
//
// WHERE THE NIGHTS COME FROM. `GET /v1/cases/:urn/allocation` is the only seam
// that carries per-group night counts (`ICaseStayView`, slice N7): the disruption
// ROSTER row does not carry `stayPlan`, and neither does the cases roster it is
// mapped from. So a disruption-wide picture is folded here, client-side, from the
// per-case allocation views of the cases the roster names — the same query key
// (`['cases', urn, 'allocation']`) the case-detail tab already uses, so the two
// share one cache entry and one invalidation. If a later slice puts `stayPlan` on
// the roster row, `useDisruptionStay` collapses to reading it and every caller
// below is unchanged.
//
// LIVENESS IS PUSH, NEVER A POLL (project rule / frontend-conventions §15).
// Three event sources keep this fresh and not one of them is a clock:
//   - `CaseChanged(kind:'allocation')` already invalidates `['cases',urn,'allocation']`;
//   - `StayPlanChanged` — the N7 fan-out — is subscribed below;
//   - a socket RE-connect (an event, not an interval) triggers one catch-up read.

/** The allocation query key, verbatim as `use-allocation.ts` writes it. */
export function caseAllocationKey(caseUrn: string): readonly unknown[] {
  return ['cases', caseUrn, 'allocation'];
}

/**
 * Cases folded into one disruption-wide stay picture. A disruption is one case
 * per disrupted flight, so this is a handful in practice; the cap is a backstop
 * against a mass event, and `truncated` says so out loud rather than quietly
 * showing a conflict count computed over half the roster.
 */
export const DISRUPTION_STAY_MAX_CASES = 24;

export interface GroupStay {
  stayPlan: StayPlanView | null;
  stayMismatch: StayMismatchView | null;
}

export interface DisruptionStay {
  /** Every planned group across the disruption's cases, keyed by sub-case URN. */
  byGroup: Map<string, GroupStay>;
  /** The flat list — what `previewStayConflicts` is computed over. */
  groups: CaseStayGroupView[];
  /** The night count every planned group agrees on; `null` when they differ. */
  nights: number | null;
  /** Groups whose plan disagrees with a live hold/reservation — humans needed. */
  mismatches: number;
  /** Groups cases derived NO plan for: they still book the 1-night default. */
  withoutPlan: number;
  isLoading: boolean;
  isError: boolean;
  /** The case fan-out hit its cap — counts below are a floor, not a total. */
  truncated: boolean;
  /** What a blanket `n` would do, computed before the operator confirms (§7). */
  preview: (nights: number) => StayConflictPreview;
}

const EMPTY_STAY: Omit<DisruptionStay, 'preview'> = {
  byGroup: new Map(),
  groups: [],
  nights: null,
  mismatches: 0,
  withoutPlan: 0,
  isLoading: false,
  isError: false,
  truncated: false,
};

/** One case's stay, off the allocation view the tab already loads. */
export function useCaseStay(caseUrn: string | undefined): {
  groups: CaseStayGroupView[];
  nights: number | null;
  mismatches: number;
  withoutPlan: number;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery<AllocationState>({
    queryKey: caseAllocationKey(caseUrn ?? ''),
    queryFn: () => getAllocation(caseUrn as string),
    enabled: !!caseUrn,
    staleTime: 30_000,
  });
  const stay = query.data?.stay;
  return {
    groups: stay?.groups ?? [],
    nights: stay?.nights ?? null,
    mismatches: stay?.mismatches ?? 0,
    withoutPlan: stay?.withoutPlan ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * ONE PNR group's stay + conflict. Used by the PNR drawer, which knows its case
 * and its sub-case but nothing about the disruption's other groups.
 *
 * `enabled` lets a caller stand the read down entirely (the drawer's hotel card
 * is rendered for PNRs that will never see a hotel); an absent plan is a
 * first-class answer meaning "no evidence — this group books the 1-night
 * default", NOT an error and NOT zero nights.
 */
export function useGroupStay(
  caseUrn: string | undefined,
  subCaseUrn: string | undefined,
  enabled = true,
): GroupStay & { isLoading: boolean; isError: boolean } {
  const query = useQuery<AllocationState>({
    queryKey: caseAllocationKey(caseUrn ?? ''),
    queryFn: () => getAllocation(caseUrn as string),
    enabled: enabled && !!caseUrn,
    staleTime: 30_000,
  });
  const group = query.data?.stay?.groups.find((g) => g.subCaseUrn === subCaseUrn);
  return {
    stayPlan: group?.stayPlan ?? null,
    stayMismatch: group?.stayMismatch ?? null,
    isLoading: query.isLoading,
    isError: query.isError || query.data?.sources?.cases === 'unavailable',
  };
}

/**
 * The whole disruption's stay, folded from its cases' allocation views.
 *
 * `caseUrns` is normally derived from the desk walk the caller already did, so
 * this adds no roster read of its own — only the per-case allocation views, which
 * the case tab shares.
 */
export function useDisruptionStay(caseUrns: readonly string[]): DisruptionStay {
  const urns = useMemo(() => {
    const distinct = [...new Set(caseUrns.filter(Boolean))].sort();
    return { list: distinct.slice(0, DISRUPTION_STAY_MAX_CASES), overflow: distinct.length > DISRUPTION_STAY_MAX_CASES };
  }, [caseUrns]);

  const results = useQueries({
    queries: urns.list.map((caseUrn) => ({
      queryKey: caseAllocationKey(caseUrn),
      queryFn: () => getAllocation(caseUrn),
      staleTime: 30_000,
    })),
  });

  // `results` is a fresh array identity on every render, so the fold keys off the
  // primitive facts it depends on instead — otherwise every parent render would
  // rebuild the map and re-render the whole desk.
  const isLoading = results.some((r) => r.isLoading);
  const isError = results.length > 0 && results.every((r) => r.isError);
  const stays = results.map((r) => (r.data as AllocationState | undefined)?.stay);
  const stamp = stays.map((s) => `${s?.groups.length ?? -1}:${s?.nights ?? 'x'}:${s?.mismatches ?? 0}`).join('|');

  const folded = useMemo(() => {
    const groups: CaseStayGroupView[] = [];
    let mismatches = 0;
    let withoutPlan = 0;
    for (const stay of stays) {
      if (!stay) continue;
      groups.push(...stay.groups);
      mismatches += stay.mismatches ?? 0;
      withoutPlan += stay.withoutPlan ?? 0;
    }
    const byGroup = new Map<string, GroupStay>(
      groups.map((g) => [g.subCaseUrn, { stayPlan: g.stayPlan, stayMismatch: g.stayMismatch }]),
    );
    return { groups, byGroup, mismatches, withoutPlan, nights: agreedNights(groups) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamp]);

  const preview = useCallback(
    (nights: number) => previewStayConflicts(nights, folded.groups),
    [folded.groups],
  );

  if (urns.list.length === 0) return { ...EMPTY_STAY, preview };
  return { ...folded, isLoading, isError, truncated: urns.overflow, preview };
}

/* ── writes ─────────────────────────────────────────────────────────────────── */

/**
 * Invalidate every read a nights change can move: the per-case allocation views
 * (the stay itself, and the rooms/spend that a longer stay re-prices) and the
 * disruption roster (the desk walks it). Predicate-based rather than per-URN
 * because a disruption-scope write touches cases this caller may not have listed.
 */
function invalidateStay(qc: QueryClient, disruptionUrn: string): void {
  void qc.invalidateQueries({
    predicate: (q) => q.queryKey[0] === 'cases' && q.queryKey[2] === 'allocation',
  });
  void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'roster'] });
}

/** The blanket "this disruption is N nights". */
export function useSetDisruptionNights(disruptionUrn: string | undefined) {
  const qc = useQueryClient();
  return useMutation<StayNightsResult, Error, number>({
    mutationFn: (nights: number) => setDisruptionStayNights(disruptionUrn as string, nights),
    onSuccess: () => {
      if (disruptionUrn) invalidateStay(qc, disruptionUrn);
    },
  });
}

/**
 * The per-PNR override. `null` CLEARS it back to the derived evidence.
 *
 * Scope-aware (see `StayNightsScope`): a PNR under a disruption writes through
 * the disruption route and nudges that disruption's roster; a PNR on a
 * standalone case writes through the case-scoped twin and nudges its own
 * PNR-group roster instead. Either way the per-case allocation views — the
 * `['cases', <urn>, 'allocation']` slice both scopes read the stay from — are
 * invalidated, so the number the operator just set is what every card shows.
 */
export function useSetGroupNights(
  scope: StayNightsScope | undefined,
  subCaseUrn: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation<StayNightsResult, Error, number | null>({
    mutationFn: (nights: number | null) =>
      setGroupStayNights(scope as StayNightsScope, subCaseUrn as string, nights),
    onSuccess: () => {
      if (scope) invalidateGroupScope(qc, scope);
    },
  });
}

/** The reads a per-PNR declaration moves, per scope: the case allocation views
 *  plus the roster the scope walks (disruption roster / case PNR-group roster). */
function invalidateGroupScope(qc: QueryClient, scope: StayNightsScope): void {
  if ('disruptionUrn' in scope) {
    invalidateStay(qc, scope.disruptionUrn);
  } else {
    void qc.invalidateQueries({
      predicate: (q) => q.queryKey[0] === 'cases' && q.queryKey[2] === 'allocation',
    });
    void qc.invalidateQueries({ queryKey: ['cases', scope.caseUrn, 'pnr-groups'] });
  }
}

/* ── the STAY-HOME declaration (care-only round 3) ──────────────────────────── */

/**
 * Declare "stays at own home" for ONE PNR (scope-aware like the nights write).
 * On success every read the suppression moves is refreshed: the case allocation
 * views (the needs-hotel gates read `requiresHotel` off them) + the scope's
 * roster (the row's `passengerElection` / `requiresHotel` denorm).
 */
export function useDeclareStayHome(
  scope: StayNightsScope | undefined,
  subCaseUrn: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation<StayHomeResult, Error, StayHomeInput | undefined>({
    mutationFn: (input) => declareStayHome(scope as StayNightsScope, subCaseUrn as string, input),
    onSuccess: () => {
      if (scope) invalidateGroupScope(qc, scope);
    },
  });
}

/** The idempotent undo of `useDeclareStayHome` — same invalidation. */
export function useRevertStayHome(
  scope: StayNightsScope | undefined,
  subCaseUrn: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation<StayHomeResult, Error, void>({
    mutationFn: () => revertStayHome(scope as StayNightsScope, subCaseUrn as string),
    onSuccess: () => {
      if (scope) invalidateGroupScope(qc, scope);
    },
  });
}

/* ── liveness ───────────────────────────────────────────────────────────────── */

/**
 * Subscribe to `operator-bff:WS StayPlanChanged@v1` for the open disruption.
 *
 * Registered DIRECTLY on the shared socket rather than through `applyEvent`,
 * because `lib/ws/{apply-event,socket}.ts` sit behind this feature's serialised
 * wiring lock and `isWsEvent`'s allow-list would drop the event before it got
 * there. The socket's public handle is a supported seam (`getSocket`), the
 * listener is scoped to this hook's lifetime, and folding this into an
 * `applyEvent` branch later is a pure move — the merge below is already written
 * as "patch nothing, invalidate what changed", which is what that branch would do.
 *
 * The payload is a PATCH, not the truth: at-least-once redelivery of the same
 * event must land on identical state, so we invalidate rather than accumulate.
 *
 * Re-registering on the `connected` transition covers both a socket that had not
 * connected yet at mount and a reconnect; it is an EVENT, never a poll.
 */
export function useStayPlanChannel(disruptionUrn: string | undefined): void {
  const qc = useQueryClient();
  const connected = useConnectionStore((s) => s.connected);

  useEffect(() => {
    if (!disruptionUrn) return;
    const socket = getSocket();
    if (!socket) return;
    const onChanged = (event: StayPlanChangedEvent): void => {
      // The BFF fans this into the `disruption:<urn>` room, but a console with two
      // disruptions open shares one socket — so match the URN before acting.
      if (!event || event.disruptionUrn !== disruptionUrn) return;
      invalidateStay(qc, disruptionUrn);
    };
    socket.on('StayPlanChanged', onChanged);
    return () => {
      socket.off('StayPlanChanged', onChanged);
    };
  }, [disruptionUrn, qc, connected]);
}
